import { useState, useEffect, useCallback } from 'react';
import { gameManagementService } from '@/services/gameManagementService';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import type { 
  Game, 
  GameWithDetails, 
  CreateGameData, 
  GameParticipant, 
  UpdateParticipantStatusData,
  LeaderboardEntry,
  GameTimeRemaining 
} from '@/types/gameSystem';
import { toast } from 'sonner';

export const useGameManagement = (userId?: string) => {
  const [games, setGames] = useState<GameWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Determine user type based on auth context
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const userType = supabaseUser ? 'supabase' : 'internal';

  const loadGames = useCallback(async () => {
    if (!userId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🎮 Loading games for user:', userId);
      const gamesData = await gameManagementService.getGamesForUser(userId, userType);
      console.log('🎮 Loaded games:', gamesData.length, gamesData);
      setGames(gamesData);
    } catch (err) {
      console.error('Error loading games:', err);
      setError('Failed to load games');
      toast.error('Failed to load games');
    } finally {
      setLoading(false);
    }
  }, [userId, userType]);

  const createGame = useCallback(async (gameData: CreateGameData): Promise<Game | null> => {
    if (!userId) {
      console.error('🎮 No userId available for game creation');
      toast.error('User must be authenticated to create a game');
      return null;
    }
    
    // Validate required fields
    if (!gameData.game_name || !gameData.game_date || !gameData.game_time) {
      toast.error('Please fill in all required fields');
      return null;
    }

    try {
      console.log('🎮 Creating game with data:', gameData);
      console.log('🎮 Using userId:', userId);
      console.log('🎮 Using userType:', userType);
      const newGame = await gameManagementService.createGame(gameData, userId, userType);
      console.log('🎮 Game created successfully:', newGame);
      await loadGames(); // Refresh the list
      return newGame;
    } catch (err) {
      console.error('Error creating game:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create game';
      toast.error(errorMessage);
      return null;
    }
  }, [userId, userType, loadGames]);

  const updateGame = useCallback(async (gameId: string, updates: Partial<Game>): Promise<boolean> => {
    try {
      await gameManagementService.updateGame(gameId, updates);
      toast.success('Game updated successfully!');
      await loadGames(); // Refresh the list
      return true;
    } catch (err) {
      console.error('Error updating game:', err);
      toast.error('Failed to update game');
      return false;
    }
  }, [loadGames]);

  const deleteGame = useCallback(async (gameId: string): Promise<boolean> => {
    try {
      await gameManagementService.deleteGame(gameId);
      toast.success('Game deleted successfully!');
      await loadGames(); // Refresh the list
      return true;
    } catch (err) {
      console.error('Error deleting game:', err);
      toast.error('Failed to delete game');
      return false;
    }
  }, [loadGames]);

  const confirmGame = useCallback(async (
    gameId: string, 
    teamSide: 'home' | 'away', 
    teamId: number
  ): Promise<boolean> => {
    if (!userId) return false;
    
    try {
      await gameManagementService.confirmGame(gameId, teamSide, teamId, userId);
      toast.success('Game confirmed successfully!');
      await loadGames(); // Refresh the list
      return true;
    } catch (err) {
      console.error('Error confirming game:', err);
      toast.error('Failed to confirm game');
      return false;
    }
  }, [userId, loadGames]);

  useEffect(() => {
    loadGames();
  }, [loadGames]);

  return {
    games,
    loading,
    error,
    createGame,
    updateGame,
    deleteGame,
    confirmGame,
    refreshGames: loadGames
  };
};

export const useGameParticipants = (gameId?: string) => {
  const [participants, setParticipants] = useState<GameParticipant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadParticipants = useCallback(async () => {
    if (!gameId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const participantsData = await gameManagementService.getGameParticipants(gameId);
      setParticipants(participantsData);
    } catch (err) {
      console.error('Error loading participants:', err);
      setError('Failed to load participants');
    } finally {
      setLoading(false);
    }
  }, [gameId]);

  const addParticipant = useCallback(async (
    participantData: Omit<GameParticipant, 'id' | 'game_id' | 'created_at' | 'updated_at' | 'status_updated_at'>
  ): Promise<boolean> => {
    if (!gameId) return false;
    
    try {
      await gameManagementService.addParticipant(gameId, participantData);
      toast.success('Player added to game!');
      await loadParticipants(); // Refresh the list
      return true;
    } catch (err) {
      console.error('Error adding participant:', err);
      toast.error('Failed to add player to game');
      return false;
    }
  }, [gameId, loadParticipants]);

  const updateParticipantStatus = useCallback(async (
    participantId: string, 
    updates: UpdateParticipantStatusData
  ): Promise<boolean> => {
    try {
      await gameManagementService.updateParticipantStatus(participantId, updates);
      toast.success('Player status updated!');
      await loadParticipants(); // Refresh the list
      return true;
    } catch (err) {
      console.error('Error updating participant status:', err);
      toast.error('Failed to update player status');
      return false;
    }
  }, [loadParticipants]);

  const removeParticipant = useCallback(async (participantId: string): Promise<boolean> => {
    try {
      await gameManagementService.removeParticipant(participantId);
      toast.success('Player removed from game!');
      await loadParticipants(); // Refresh the list
      return true;
    } catch (err) {
      console.error('Error removing participant:', err);
      toast.error('Failed to remove player from game');
      return false;
    }
  }, [loadParticipants]);

  useEffect(() => {
    loadParticipants();
  }, [loadParticipants]);

  return {
    participants,
    loading,
    error,
    addParticipant,
    updateParticipantStatus,
    removeParticipant,
    refreshParticipants: loadParticipants
  };
};

export const useLeaderboard = () => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLeaderboard = useCallback(async (
    entityType?: 'player' | 'coach' | 'team',
    sportId?: number,
    seasonYear?: number
  ) => {
    setLoading(true);
    setError(null);
    
    try {
      const leaderboardData = await gameManagementService.getLeaderboard(entityType, sportId, seasonYear);
      setLeaderboard(leaderboardData);
    } catch (err) {
      console.error('Error loading leaderboard:', err);
      setError('Failed to load leaderboard');
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const getEntityStats = useCallback(async (
    entityId: string,
    entityType: 'player' | 'coach' | 'team',
    entitySubtype: 'supabase' | 'internal' = 'supabase'
  ): Promise<LeaderboardEntry | null> => {
    try {
      return await gameManagementService.getEntityStats(entityId, entityType, entitySubtype);
    } catch (err) {
      console.error('Error getting entity stats:', err);
      return null;
    }
  }, []);

  return {
    leaderboard,
    loading,
    error,
    loadLeaderboard,
    getEntityStats
  };
};

export const useGameCountdown = (countdownEndAt?: string) => {
  const [timeRemaining, setTimeRemaining] = useState<GameTimeRemaining | null>(null);

  useEffect(() => {
    if (!countdownEndAt) {
      setTimeRemaining(null);
      return;
    }

    const updateCountdown = () => {
      const remaining = gameManagementService.calculateTimeRemaining(countdownEndAt);
      setTimeRemaining(remaining);
    };

    // Update immediately
    updateCountdown();

    // Update every second
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [countdownEndAt]);

  return timeRemaining;
};